import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { X, Loader2, Sparkles, CheckCircle2 } from "lucide-react";
import { fmt } from "@/lib/format";

export default function BulkSozlesmeDialog({ open, onClose, onDone }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [rows, setRows] = useState(null);
  const [done, setDone] = useState(null);

  if (!open) return null;

  const extractAll = async () => {
    if (!files.length) { toast.error("En az bir sözleşme seçin"); return; }
    setBusy(true);
    const out = [];
    for (let i = 0; i < files.length; i++) {
      setProgress(`Okunuyor ${i + 1}/${files.length}...`);
      try {
        const fd = new FormData();
        fd.append("file", files[i]);
        const { data } = await api.post("/cari/sozlesme-extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
        out.push({ filename: files[i].name, data, af: "" });
      } catch (e) {
        out.push({ filename: files[i].name, error: e.response?.data?.detail || "Okunamadı" });
      }
    }
    setProgress("");
    setRows(out);
    setBusy(false);
  };

  const createAll = async () => {
    setBusy(true);
    let created = 0, fail = 0;
    for (const r of rows) {
      if (!r.data || !r.data.musteri) { fail++; continue; }
      try {
        await api.post("/cari/sozlesme-create", {
          musteri: r.data.musteri, tc_no: r.data.tc_no, adres: r.data.adres, telefon: r.data.telefon,
          ikinci_borclu_ad: r.data.ikinci_borclu_ad, ikinci_borclu_tc: r.data.ikinci_borclu_tc, urun: r.data.urun,
          satis_fiyati: Number(r.data.satis_fiyati) || 0, alis_fiyati: Number(r.af) || 0, pesinat: Number(r.data.pesinat) || 0,
          vadeler: (r.data.vadeler || []).map((v) => ({ vade_tarihi: v.vade_tarihi, tutar: Number(v.tutar) || 0 })),
        });
        created++;
      } catch { fail++; }
    }
    setDone({ created, fail });
    setBusy(false);
    onDone && onDone();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="bulk-sozlesme-dialog">
      <div className="bg-white rounded-sm border border-[#E7DECC] w-full max-w-3xl shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E7DECC] sticky top-0 bg-white">
          <h2 className="font-heading text-lg font-bold tracking-tight flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#7C5E9B]" /> Toplu Sözleşme Yükleme</h2>
          <button onClick={onClose} data-testid="bulk-soz-close" className="p-1 hover:bg-[#EFE7D6] rounded-sm"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          {!rows && !done && (
            <>
              <p className="text-sm text-[#8C8377]">Birden fazla sözleşme (JPG/PNG/PDF) seçin; her biri otomatik okunur. Ardından her müşteri için sadece <b>Alış Fiyatı</b>'nı girip toplu oluşturursunuz.</p>
              <input type="file" multiple accept=".jpg,.jpeg,.png,.pdf" data-testid="bulk-soz-files" onChange={(e) => setFiles(Array.from(e.target.files))} className="block w-full text-sm text-[#8C8377] file:mr-3 file:py-2 file:px-4 file:rounded-sm file:border-0 file:bg-[#EFE7D6] file:text-sm file:font-medium file:cursor-pointer hover:file:bg-[#E7DECC]" />
              {files.length > 0 && <div className="text-sm text-[#8C8377]">{files.length} dosya seçildi</div>}
            </>
          )}
          {busy && progress && <div className="text-sm text-[#B8933F] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {progress}</div>}
          {rows && !done && (
            <div className="space-y-2 max-h-[50vh] overflow-auto" data-testid="bulk-soz-rows">
              {rows.map((r, i) => r.error ? (
                <div key={i} className="text-sm text-[#A63A3A] border border-[#A63A3A]/30 bg-[#A63A3A]/5 rounded-sm px-3 py-2">{r.filename}: {r.error}</div>
              ) : (
                <div key={i} className="flex items-center gap-3 justify-between border border-[#E7DECC] rounded-sm px-3 py-2">
                  <div className="text-sm min-w-0">
                    <b className="text-[#B8933F]">{r.data.musteri || "?"}</b>
                    <span className="text-[#8C8377]"> · {(r.data.vadeler || []).length} vade · SF {fmt(Number(r.data.satis_fiyati) || 0)}{r.data.ikinci_borclu_ad ? ` · 2.borçlu: ${r.data.ikinci_borclu_ad}` : ""}</span>
                  </div>
                  <input placeholder="Alış Fiyatı" value={r.af} onChange={(e) => { const n = [...rows]; n[i] = { ...n[i], af: e.target.value }; setRows(n); }} data-testid={`bulk-soz-af-${i}`} className="w-32 px-2 py-1.5 border border-[#B8933F] rounded-sm text-sm text-right shrink-0" />
                </div>
              ))}
            </div>
          )}
          {done && (
            <div className="text-sm border border-[#2F6E4E]/40 bg-[#2F6E4E]/5 rounded-sm p-3 flex items-center gap-2 text-[#2F6E4E] font-semibold" data-testid="bulk-soz-done">
              <CheckCircle2 className="w-4 h-4" /> {done.created} müşteri oluşturuldu{done.fail ? `, ${done.fail} başarısız` : ""}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-[#E7DECC] sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 border border-[#E7DECC] rounded-sm text-sm font-medium hover:bg-[#EFE7D6]">{done ? "Kapat" : "İptal"}</button>
          {!rows && !done && (
            <button onClick={extractAll} disabled={busy || !files.length} data-testid="bulk-soz-extract" className="flex items-center gap-2 px-4 py-2 bg-[#7C5E9B] hover:bg-[#9A3FC7] text-white rounded-sm text-sm font-medium disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Tümünü Oku
            </button>
          )}
          {rows && !done && (
            <button onClick={createAll} disabled={busy} data-testid="bulk-soz-create" className="flex items-center gap-2 px-4 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Tümünü Oluştur ({rows.filter((r) => r.data).length})
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
